import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Box,
  Typography,
  Paper,
  Avatar,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../../styles/portalListTable';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatPhoneDisplay } from '../../../utils/formatPhoneDisplay';
import DeleteAttendeeDialog from '../../Partner/Events/widgets/DeleteAttendeeDialog';
import {
  formatGuestPaidAt,
  getGuestAttendeeId,
  GUEST_LIST_ROWS_PER_PAGE,
} from '../../Partner/Events/widgets/partnerEventGuestListUtils';
import { GuestListAddAttendeeAction } from './AddAttendeeModal';

const PAYMENT_BADGE_STYLES = {
  Paid: { bg: '#E8F5E9', color: '#2E7D32' },
  'Out of Platform': { bg: '#EDE7F6', color: '#5E35B1' },
  Pending: { bg: '#FFF3E0', color: '#E65100' },
  Refunded: { bg: '#FCE4EC', color: '#C62828' },
  Bypassed: { bg: '#E3F2FD', color: '#1565C0' },
};

const PaymentBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'paletteKey',
})(({ paletteKey }) => {
  const { bg, color } = PAYMENT_BADGE_STYLES[paletteKey] ?? { bg: '#f5f5f5', color: '#616161' };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 14px',
    borderRadius: 9999,
    backgroundColor: bg,
    color,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
});

const RefundButton = styled(LoadingButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  borderColor: theme.palette.divider,
  borderRadius: 8,
  padding: '6px 14px',
  '&:hover': {
    borderColor: theme.palette.text.disabled,
    backgroundColor: theme.palette.background.default,
  },
}));

const DeleteButton = styled(LoadingButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.error.main,
  borderColor: theme.palette.error.light,
  borderRadius: 8,
  padding: '6px 14px',
  '&:hover': {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
}));

const EventGuestListTable = ({
  guests,
  eventId,
  showAddAttendee = false,
  onRefund,
  onDeleteAttendee,
  refundingBookingId = null,
  deletingAttendeeId = null,
}) => {
  const showRefundActions = Boolean(onRefund);
  const showDeleteActions = Boolean(onDeleteAttendee);
  const hasOutOfPlatformGuests = useMemo(
    () => guests.some((guest) => guest.outOfPlatform),
    [guests],
  );
  const showActions = showRefundActions || (showDeleteActions && hasOutOfPlatformGuests);
  const isAnyRefundInProgress = Boolean(refundingBookingId);
  const isAnyDeleteInProgress = Boolean(deletingAttendeeId);
  const [page, setPage] = useState(0);
  const [attendeeToDelete, setAttendeeToDelete] = useState(null);

  const paginatedGuests = useMemo(() => {
    const start = page * GUEST_LIST_ROWS_PER_PAGE;
    return guests.slice(start, start + GUEST_LIST_ROWS_PER_PAGE);
  }, [guests, page]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(guests.length / GUEST_LIST_ROWS_PER_PAGE) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [guests.length, page]);

  const handleConfirmDelete = async () => {
    const attendeeId = getGuestAttendeeId(attendeeToDelete);
    if (!attendeeId || !onDeleteAttendee || isAnyDeleteInProgress) return;

    try {
      await onDeleteAttendee(attendeeId);
      setAttendeeToDelete(null);
    } catch {
      // Parent handles error toast.
    }
  };

  if (!guests.length) {
    return (
      <Box sx={{ mt: 2 }}>
        {showAddAttendee ? <GuestListAddAttendeeAction eventId={eventId} /> : null}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No guests yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {showAddAttendee ? <GuestListAddAttendeeAction eventId={eventId} /> : null}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Table>
          <PortalTableHead>
            <TableRow>
              <TableCell>Guest</TableCell>
              <TableCell>Contacts</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Paid At</TableCell>
              {showActions ? <TableCell align="right">Actions</TableCell> : null}
            </TableRow>
          </PortalTableHead>
          <TableBody>
            {paginatedGuests.map((guest, index) => {
              const bookingKey = guest.bookingId ?? guest.id;
              const attendeeId = getGuestAttendeeId(guest);
              const isRowRefunding = refundingBookingId === bookingKey;
              const isRowDeleting = deletingAttendeeId === attendeeId;
              const disableRefund =
                guest.paymentStatus !== 'Paid' || (isAnyRefundInProgress && !isRowRefunding);
              const disableDelete = isAnyDeleteInProgress && !isRowDeleting;

              return (
                <PortalTableRow key={guest.id}>
                  <PortalTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={guest.avatarUrl || undefined}
                        alt={guest.name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {!guest.avatarUrl && guest.name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {guest.name}
                        </Typography>
                        {guest.profession ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {guest.profession}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </PortalTableCell>
                  <PortalTableCell>
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {guest.email !== '—' ? guest.email : '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {formatPhoneDisplay(guest.phone)}
                    </Typography>
                  </PortalTableCell>
                  <PortalTableCell>
                    <PaymentBadge paletteKey={guest.paymentStatus}>
                      {guest.paymentStatus}
                    </PaymentBadge>
                  </PortalTableCell>
                  <PortalTableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      {formatGuestPaidAt(guest.paidAt)}
                    </Typography>
                  </PortalTableCell>
                  {showActions ? (
                    <PortalTableCell align="right">
                      {guest.outOfPlatform && showDeleteActions ? (
                        <DeleteButton
                          variant="outlined"
                          size="small"
                          loading={isRowDeleting}
                          disabled={disableDelete}
                          loadingPosition="start"
                          startIcon={
                            !isRowDeleting ? <DeleteOutlineIcon sx={{ fontSize: 18 }} /> : undefined
                          }
                          onClick={() => setAttendeeToDelete(guest)}
                        >
                          {isRowDeleting ? 'Deleting…' : 'Delete'}
                        </DeleteButton>
                      ) : showRefundActions ? (
                        <RefundButton
                          variant="outlined"
                          size="small"
                          loading={isRowRefunding}
                          disabled={disableRefund}
                          loadingPosition="start"
                          startIcon={
                            !isRowRefunding ? <ReplayIcon sx={{ fontSize: 18 }} /> : undefined
                          }
                          onClick={() => onRefund(bookingKey)}
                        >
                          {isRowRefunding ? 'Refunding…' : 'Refund'}
                        </RefundButton>
                      ) : null}
                    </PortalTableCell>
                  ) : null}
                </PortalTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={guests.length}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={GUEST_LIST_ROWS_PER_PAGE}
        rowsPerPageOptions={[GUEST_LIST_ROWS_PER_PAGE]}
        onRowsPerPageChange={() => {}}
      />

      <DeleteAttendeeDialog
        open={Boolean(attendeeToDelete)}
        attendee={attendeeToDelete}
        deleting={isAnyDeleteInProgress}
        onClose={() => {
          if (!isAnyDeleteInProgress) setAttendeeToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default EventGuestListTable;
